import { AnalyticsService } from '@alison/analytics/client';
import { FormFieldLabel } from '@alison/ui';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import ViewListOutlined from '@mui/icons-material/ViewListOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { dateWindowFromRange } from '../../lib/dateUtils.js';
import { useClients } from '../../lib/filters.hooks.js';
import { formatRelativeTime } from '../../lib/notificationFeed.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';
import { useOutcomes } from '../../lib/workflows.hooks.js';
import { AppShell } from '../AppShell.js';
import { MenuSearchInput } from '../filters/MenuSearchInput.js';
import { FILTER_BAR_ROW_SX } from '../filters/menuStyles.js';
import { SingleSelectControl } from '../filters/SingleSelectControl.js';
import type { ScopeState } from '../filters/types.js';
import { useScopeAccounts } from '../filters/useScopeSelection.js';
import { OutcomeDrawer } from '../workflows/OutcomeDrawer.js';
import type { ReportScope } from '../workflows/reportMeta.js';
import type { MapOutcome } from '../workflows/sessionRows.js';
import { OutcomeCard } from './OutcomeCard.js';
import { OutcomesScopeBar } from './OutcomesScopeBar.js';
import { OutcomesTable } from './OutcomesTable.js';
import {
  choicesOf,
  groupedBySession,
  type Outcome,
  type OutcomeFilters,
  type OutcomeOrder,
  shownOutcomes,
} from './outcomes.js';

const PAGE_LIMIT = 50;
// How far back a link's run is looked for before the page gives up on opening it.
const LINK_SEARCH_PAGES = 10;

const RECENT_MS = 48 * 60 * 60 * 1000;

const NAV_TONE_SX = {
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'background.icon' },
  '& .MuiToggleButton-root.Mui-selected': {
    bgcolor: 'background.icon',
    '&:hover': { bgcolor: 'background.icon' },
  },
};

interface OpenedOutcome {
  outcome: MapOutcome;
  scope: ReportScope;
  focus?: string | null;
}

const ALL = 'all';

const KINDS = [
  { id: ALL, label: 'All outcomes' },
  { id: 'report', label: 'Reports' },
  { id: 'brief', label: 'Briefs' },
  { id: 'storyboard', label: 'Storyboards' },
  { id: 'video', label: 'Videos' },
  { id: 'banner', label: 'Banners' },
];

const ORDERS: Array<{ id: OutcomeOrder; label: string }> = [
  { id: 'latest', label: 'Latest first' },
  { id: 'oldest', label: 'Oldest first' },
];

const GRID_SX = {
  display: 'grid',
  gap: 3,
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
};

function namedChoices(names: string[]): Array<{ id: string; label: string }> {
  return [{ id: ALL, label: 'All' }, ...names.map((name) => ({ id: name, label: name }))];
}

export interface OutcomesPageProps {
  scope: ScopeState;
  onScopeChange: (scope: ScopeState) => void;
  // The run a link arrived asking for, opened once its page has loaded.
  openRunId?: string | null;
  // The brief, by creative id, that link lands on within a run of several.
  openBrief?: string | null;
}

export function OutcomesPage({
  scope,
  onScopeChange,
  openRunId = null,
  openBrief = null,
}: OutcomesPageProps): ReactNode {
  const { session, isPending: loadingSession, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const { outcomes, pagesLoaded, isPending, hasMore, isLoadingMore, loadMore } =
    useOutcomes(PAGE_LIMIT);
  const [opened, setOpened] = useState<OpenedOutcome | null>(null);
  const { data: clients } = useClients();
  const [filters, setFilters] = useState<OutcomeFilters>({
    search: '',
    kind: null,
    workflow: null,
    creator: null,
  });
  const [order, setOrder] = useState<OutcomeOrder>('latest');
  const [grouped, setGrouped] = useState(false);
  const [dense, setDense] = useState(true);

  const accounts = useMemo(() => (clients ?? []).flatMap((client) => client.accounts), [clients]);
  const { accountIds } = useScopeAccounts(scope);
  const [from, to] = useMemo(() => dateWindowFromRange(scope.range, new Date()), [scope.range]);
  const shown = shownOutcomes(outcomes, filters, order, { accountIds, from, to });

  const platformsOf = (outcome: Outcome): string[] => [
    ...new Set(
      outcome.accountIds.flatMap((id) => {
        const match = accounts.find((row) => row.id === id);
        return match?.network == null ? [] : [match.network];
      }),
    ),
  ];
  const brandOf = (outcome: Outcome): string | null => {
    const owner = (clients ?? []).find((client) =>
      client.accounts.some((row) => outcome.accountIds.includes(row.id)),
    );
    return owner?.name ?? null;
  };
  const scopeOf = (outcome: Outcome): ReportScope => ({
    startDate: outcome.startDate,
    endDate: outcome.endDate,
    accountIds: outcome.accountIds,
  });
  const whenOf = (outcome: Outcome): string => {
    const date = new Date(outcome.completedAt);
    return Date.now() - date.getTime() < RECENT_MS
      ? formatRelativeTime(date)
      : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const requested =
    openRunId === null ? undefined : outcomes.find((row) => row.runId === openRunId);
  // Remembered so closing the drawer does not reopen what the link asked for.
  const [linked, setLinked] = useState<string | null>(null);
  useEffect(() => {
    if (requested === undefined || linked === requested.runId) return;
    setLinked(requested.runId);
    setOpened({
      outcome: { runId: requested.runId, name: requested.title, kind: requested.kind, date: null },
      scope: {
        startDate: requested.startDate,
        endDate: requested.endDate,
        accountIds: requested.accountIds,
      },
      focus: openBrief,
    });
  }, [requested, linked, openBrief]);
  const searching =
    openRunId !== null &&
    requested === undefined &&
    hasMore &&
    !isLoadingMore &&
    pagesLoaded < LINK_SEARCH_PAGES;
  useEffect(() => {
    if (searching) loadMore();
  }, [searching, loadMore]);

  if (loadingSession || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const open = (outcome: Outcome): void => {
    AnalyticsService.track('Outcomes - Outcome Opened', { kind: outcome.kind });
    setOpened({
      outcome: { runId: outcome.runId, name: outcome.title, kind: outcome.kind, date: null },
      scope: scopeOf(outcome),
    });
  };
  const openSource = (outcome: Outcome, source: Outcome['sources'][number]): void => {
    AnalyticsService.track('Outcomes - Source Opened', { kind: source.kind });
    setOpened({
      outcome: { runId: source.runId, name: source.title, kind: source.kind, date: null },
      scope: scopeOf(outcome),
    });
  };
  const changeFilters = (filter: string, next: typeof filters): void => {
    AnalyticsService.track('Outcomes - Filter Changed', { filter });
    setFilters(next);
  };

  const rowsOf = (rows: Outcome[]): ReactNode =>
    dense ? (
      <OutcomesTable
        rows={rows.map((outcome) => ({
          outcome,
          platforms: platformsOf(outcome),
          when: whenOf(outcome),
        }))}
        onOpen={open}
        onOpenSource={openSource}
      />
    ) : (
      <Box sx={GRID_SX}>
        {rows.map((outcome) => (
          <OutcomeCard
            key={`${outcome.runId}|${outcome.kind}`}
            outcome={outcome}
            platforms={platformsOf(outcome)}
            brand={brandOf(outcome)}
            when={whenOf(outcome)}
            onOpen={() => open(outcome)}
            onOpenSource={(source) => openSource(outcome, source)}
          />
        ))}
      </Box>
    );

  return (
    <AppShell
      active="outcomes"
      account={account}
      onLogout={() => void onSignOut()}
      topBar={<OutcomesScopeBar scope={scope} onChange={onScopeChange} />}
      sx={{ mx: 34, py: 6 }}
    >
      <Stack sx={{ gap: 3 }}>
        <Box>
          <Typography variant="h2">Outcomes</Typography>
          <Typography sx={{ color: 'text.muted' }}>
            Every report and brief your workflows have produced.
          </Typography>
        </Box>

        <Stack
          direction="row"
          sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}
        >
          <Box sx={{ ...FILTER_BAR_ROW_SX, alignItems: 'normal' }}>
            <Box sx={{ display: 'flex', width: 210 }}>
              <MenuSearchInput
                placeholder="Search"
                value={filters.search}
                onChange={(search) => changeFilters('search', { ...filters, search })}
              />
            </Box>
            <Box sx={{ display: 'inline-flex' }}>
              <SingleSelectControl
                label="TYPE"
                options={KINDS}
                value={filters.kind ?? ALL}
                onChange={(kind) =>
                  changeFilters('kind', { ...filters, kind: kind === ALL ? null : kind })
                }
              />
            </Box>
            <SingleSelectControl
              label="WORKFLOW"
              options={namedChoices(choicesOf(outcomes, (outcome) => outcome.workflow))}
              value={filters.workflow ?? ALL}
              onChange={(workflow) =>
                changeFilters('workflow', {
                  ...filters,
                  workflow: workflow === ALL ? null : workflow,
                })
              }
            />
            <SingleSelectControl
              label="CREATOR"
              options={namedChoices(choicesOf(outcomes, (outcome) => outcome.creator))}
              value={filters.creator ?? ALL}
              onChange={(creator) =>
                changeFilters('creator', { ...filters, creator: creator === ALL ? null : creator })
              }
            />
          </Box>

          <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
            <SingleSelectControl
              label="SORT"
              options={ORDERS}
              value={order}
              onChange={(next) => {
                AnalyticsService.track('Outcomes - Filter Changed', { filter: 'order' });
                setOrder(next === 'oldest' ? 'oldest' : 'latest');
              }}
            />
            <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
              <Switch
                size="small"
                checked={grouped}
                onChange={(event) => {
                  AnalyticsService.track('Outcomes - Grouping Toggled', {
                    grouped: event.target.checked,
                  });
                  setGrouped(event.target.checked);
                }}
                slotProps={{ input: { 'aria-label': 'Group by session' } }}
                sx={NAV_TONE_SX}
              />
              <Typography sx={{ color: 'text.muted' }}>Group by session</Typography>
            </Stack>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dense ? 'list' : 'grid'}
              onChange={(_event, next) => {
                if (next !== null) setDense(next === 'list');
              }}
              sx={NAV_TONE_SX}
            >
              <ToggleButton value="grid" aria-label="Grid view">
                <GridViewOutlined sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="list" aria-label="List view">
                <ViewListOutlined sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Box data-tour="outcomes-list">
          {isPending ? (
            <Stack sx={{ py: 6, alignItems: 'center' }}>
              <CircularProgress />
            </Stack>
          ) : shown.length === 0 ? (
            <Typography sx={{ py: 6, color: 'text.muted', textAlign: 'center' }}>
              No outcomes yet. Run a workflow and its report or brief lands here.
            </Typography>
          ) : grouped ? (
            <Stack sx={{ gap: 4 }}>
              {groupedBySession(shown).map((group) => (
                <Stack key={group.sessionId} sx={{ gap: 2 }}>
                  <FormFieldLabel sx={{ color: 'text.muted' }}>
                    {`SESSION · ${group.outcomes.length} OUTCOME${group.outcomes.length === 1 ? '' : 'S'}`}
                  </FormFieldLabel>
                  {rowsOf(group.outcomes)}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Stack sx={{ gap: 2 }}>
              <FormFieldLabel sx={{ color: 'text.muted' }}>
                {`ALL OUTCOMES (${shown.length}${hasMore ? '+' : ''})`}
              </FormFieldLabel>
              {rowsOf(shown)}
            </Stack>
          )}
        </Box>

        {isPending || !hasMore ? null : (
          <Stack sx={{ alignItems: 'center' }}>
            <Button variant="outlined" disabled={isLoadingMore} onClick={loadMore}>
              {isLoadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Stack>
        )}
      </Stack>

      <OutcomeDrawer
        outcome={opened?.outcome ?? null}
        scope={opened?.scope ?? null}
        focus={opened?.focus ?? null}
        leadingKpi={scope.leadingKpi}
        onClose={() => setOpened(null)}
      />
    </AppShell>
  );
}
